/**
 * MollieCoursePanel — Drei-Zustands-Control für die Online-Zahlung (Phase 3, Task P3-1)
 *
 * Eigene visuelle Einheit oberhalb der "Standard-Zahlweise"-Sektion im
 * Kurspreis-Tab des Kurs-Wizard-Steps. Statt eines einfachen An/Aus-Switch bildet
 * die Komponente jetzt die DREI Kurs-Zustände als ein einziges Segmented-Control ab
 * (Adversarial-Designentscheidung R6: ein 3-Wege-Control schließt den unmöglichen
 * Zustand `mollieOptional=true` ohne `allowMolliePayment` aus):
 *
 *   - "Aus"                ⇒ {allowMolliePayment:false, mollieOptional:false}
 *   - "Verpflichtend"      ⇒ {allowMolliePayment:true,  mollieOptional:false}
 *   - "Zusätzlich erlauben"⇒ {allowMolliePayment:true,  mollieOptional:true}
 *
 * Das Mapping ist als reine, getestete Funktion `mapMolliePanelModeToFlags`
 * gekapselt. Beim Aktivieren (verpflichtend ODER optional) wird
 * `allowUpFrontPayment=true` gesetzt (Entscheidung A: online-fähige Kurse sind
 * Vorkasse-only).
 *
 * Entscheidung B (sichtbare Sperre): Online und monatlich schließen sich aus. Ist
 * `allowMonthlyPayment===true`, wird das Control disabled mit Tooltip
 * (`monthlyConflictTooltip`) — KEIN stilles Zurücksetzen von `allowMonthlyPayment`
 * mehr (das frühere `setFieldValue("allowMonthlyPayment", false)` ist entfernt).
 *
 * Der Online-Zahlungs-Modus darf jederzeit umgeschaltet werden — auch bei Kursen
 * mit bereits vorhandenen Buchungen. Bestandsbuchungen bleiben dabei über ihre
 * eingefrorenen ScheduledPayment-Daten unberührt; die frühere `bookingCount`-Sperre
 * (Locked-State) ist daher entfernt. Einzig die monatlich↔online-Sperre bleibt
 * bestehen.
 *
 * Sichtbar NUR wenn der Caller (`StepCoursePrice.tsx`) das Gating an
 * `subscription.featureMollie === true` durchgesetzt hat — diese Komponente macht
 * KEINEN eigenen Feature-Flag-Read.
 *
 * RHL-Regel (R15): i18n-Proxy IMMER im Funktionskörper — kein module-level
 * `const t = i18n.<path>`.
 *
 * React 17 / AntD 4 / Formik 2. Node 14 kompatibel — keine `arr.at`, kein
 * `replaceAll`, kein `useId`, kein `satisfies`.
 */

import React, {FC} from "react";
import {Segmented, Tooltip, Typography} from "antd";
import {FormikProps} from "formik";
import {EditCourseInput, NewCourseInput, UpdateCourseAndBookingsInput} from "../../../generated/graphql";
import i18n from "../../../services/i18n";
import {mollieMethodLabel} from "../../../helpers/mollieMethodLabels";
import css from "./MollieCoursePanel.less";

/** Die drei Modi des Panels (1:1 zu den i18n-Keys `molliePayment.modes.*`). */
export type MolliePanelMode = "off" | "mandatory" | "optional";

/** Die von einem Modus abgeleiteten Kurs-Flags. */
export type MolliePanelFlags = {
    allowMolliePayment: boolean;
    mollieOptional: boolean;
};

/**
 * Reine Mapping-Funktion Modus ⇒ Flags (Task P3-1, R6). Schließt den unmöglichen
 * Zustand (`mollieOptional=true` ohne `allowMolliePayment`) per Konstruktion aus.
 */
export const mapMolliePanelModeToFlags = (mode: MolliePanelMode): MolliePanelFlags => {
    switch (mode) {
        case "mandatory":
            return {allowMolliePayment: true, mollieOptional: false};
        case "optional":
            return {allowMolliePayment: true, mollieOptional: true};
        case "off":
        default:
            return {allowMolliePayment: false, mollieOptional: false};
    }
};

/** Umkehrung: aus den Flags den Anzeige-Modus ableiten. */
export const deriveMolliePanelMode = (flags: {
    allowMolliePayment?: boolean | null;
    mollieOptional?: boolean | null;
}): MolliePanelMode => {
    if (flags.allowMolliePayment !== true) {
        return "off";
    }
    return flags.mollieOptional === true ? "optional" : "mandatory";
};

type Props = {
    formikProps: FormikProps<NewCourseInput | EditCourseInput | UpdateCourseAndBookingsInput>;
    style?: React.CSSProperties;
    /** In den Einstellungen freigeschaltete Mollie-Methoden des Tenants (`settings.enabledMollieMethods`). */
    enabledMollieMethods?: Array<string> | null;
};

const MollieCoursePanel: FC<Props> = ({formikProps, style, enabledMollieMethods}) => {
    // RHL-Regel + Proxy-Statefulness (siehe ResetPaymentPlanModal.tsx:160): i18n
    // IMMER als voller Pfad PRO Aufruf — KEINE Zwischenreferenz `const t = i18n.x.y`
    // (der Proxy resettet `path` nach jedem apply; nur der erste Aufruf würde
    // funktionieren).

    const allowMolliePayment = formikProps.values.allowMolliePayment === true;
    // mollieOptional ist im generierten Typ vorhanden; defensiver Read über die
    // Union-Werte (alle drei Input-Typen deklarieren das Feld).
    const mollieOptional = formikProps.values.mollieOptional === true;
    const isActive = allowMolliePayment;
    const isMonthlyActive = formikProps.values.allowMonthlyPayment === true;

    // Modus aus den aktuellen Flags ableiten.
    const currentMode: MolliePanelMode = deriveMolliePanelMode({allowMolliePayment, mollieOptional});

    // Entscheidung B: bei aktiver monatlicher Bezahlung ist das Control gesperrt
    // (sichtbare Sperre + Tooltip). Eine Buchungs-Sperre gibt es bewusst NICHT mehr —
    // der Online-Zahlungs-Modus darf jederzeit umgeschaltet werden.
    const isControlDisabled = isMonthlyActive;

    const handleChange = (value: string | number) => {
        const mode = value as MolliePanelMode;
        const flags = mapMolliePanelModeToFlags(mode);
        formikProps.setFieldValue("allowMolliePayment", flags.allowMolliePayment);
        formikProps.setFieldValue("mollieOptional", flags.mollieOptional);
        if (flags.allowMolliePayment) {
            // Entscheidung A: online-fähige Kurse sind Vorkasse-only. Vorkasse wird
            // beim Aktivieren erzwungen. KEIN stilles Zurücksetzen von
            // allowMonthlyPayment mehr (Entscheidung B: sichtbare Sperre statt
            // lautlosem Override).
            formikProps.setFieldValue("allowUpFrontPayment", true);
        }
    };

    const options = [
        {label: i18n.containers.courses.CourseCreate.molliePayment.modes.off.label(), value: "off"},
        {label: i18n.containers.courses.CourseCreate.molliePayment.modes.mandatory.label(), value: "mandatory"},
        {label: i18n.containers.courses.CourseCreate.molliePayment.modes.optional.label(), value: "optional"},
    ];

    const panelClassName = isActive ? `${css.molliePanel} ${css.active}` : css.molliePanel;

    // Modus-spezifischer Hinweis unter dem Control (voller Pfad pro Aufruf).
    const modeHint =
        currentMode === "mandatory"
            ? i18n.containers.courses.CourseCreate.molliePayment.modes.mandatory.hint()
            : currentMode === "optional"
            ? i18n.containers.courses.CourseCreate.molliePayment.modes.optional.hint()
            : i18n.containers.courses.CourseCreate.molliePayment.modes.off.hint();

    // Nur die Methoden des Tenants nennen; ohne freigeschaltete Methode der Satz ohne Liste.
    const methods = (enabledMollieMethods || []).map(mollieMethodLabel).join(", ");
    const description = methods
        ? i18n.containers.courses.CourseCreate.molliePayment.sectionDescriptionWithMethods({methods})
        : i18n.containers.courses.CourseCreate.molliePayment.sectionDescription();

    const segmented = (
        <Segmented
            data-testid="MollieModeSegmented"
            options={options}
            value={currentMode}
            onChange={handleChange}
            disabled={isControlDisabled}
        />
    );

    return (
        <div className={panelClassName} style={style} data-testid="MolliePanel">
            <div className={css.molliePanelHead}>
                <span className={css.mollieLogo}>{"mollie"}</span>
                <span className={css.mollieTitle}>{i18n.containers.courses.CourseCreate.molliePayment.sectionTitle()}</span>
                {isMonthlyActive ? (
                    <Tooltip title={i18n.containers.courses.CourseCreate.molliePayment.monthlyConflictTooltip()}>{segmented}</Tooltip>
                ) : (
                    segmented
                )}
            </div>
            <div className={css.mollieDesc} data-testid="MollieDescription">
                {description}
            </div>
            <div className={css.mollieModeHint} data-testid="MollieModeHint">
                {modeHint}
            </div>
            {isActive && (
                <div className={css.mollieInfoStrip} data-testid="MollieUpfrontForcedHint">
                    {i18n.containers.courses.CourseCreate.molliePayment.upfrontForcedHint()}
                </div>
            )}
            {isMonthlyActive && (
                <Typography.Text type="warning" className={css.lockedHint} data-testid="MollieMonthlyConflictHint">
                    {i18n.containers.courses.CourseCreate.molliePayment.monthlyConflictTooltip()}
                </Typography.Text>
            )}
        </div>
    );
};

export default MollieCoursePanel;
